import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
    omit,
    generateIsomorphicID,
    generateFakeOnChangeEvent,
} from 'wanderu-utils';

import ContextHelp from './ContextHelp/ContextHelp';
import InputIndicators from './InputIndicators/InputIndicators';

import styles from './FormInput.scss';

const INPUT_PROP_BLACKLIST = [
    'className',
    'inputClassName',
    'contextHelpClassName',
    'clearable',
    'showValidity',
    'showInputCaret',
    'valid',
    'helpMessage',
    'placeholder',
    'type',
    'valid',
    'onFocus',
    'onBlur',
    'onKeyDown',
    'digitsOnly',
];

const NON_TEXT_INPUT_TYPES = ['checkbox', 'radio', 'select'];

class FormInput extends React.Component {
    static propTypes = {
        autoFocus: PropTypes.bool,
        className: PropTypes.string,
        contextHelpClassName: PropTypes.string,
        clearable: PropTypes.bool,
        helpMessage: PropTypes.string,
        inputClassName: PropTypes.string,
        onBlur: PropTypes.func,
        onFocus: PropTypes.func,
        onKeyDown: PropTypes.func,
        placeholder: PropTypes.string,
        readOnly: PropTypes.bool,
        showValidity: PropTypes.bool,
        showInputCaret: PropTypes.bool,
        type: PropTypes.string,
        valid: PropTypes.bool,
        value: PropTypes.string,
        id: PropTypes.string,
    };

    static defaultProps = {
        autoFocus: false,
        className: '',
        contextHelpClassName: '',
        clearable: true,
        helpMessage: '',
        inputClassName: '',
        onBlur() { },
        onFocus() { },
        onKeyDown() { },
        placeholder: '',
        readOnly: false,
        showValidity: false,
        showInputCaret: false,
        type: 'text',
        valid: true,
        value: undefined,
    };

    constructor(props) {
        super(props);

        this.input = undefined;

        this.state = {
            id: props.id || generateIsomorphicID(props),
            isFocused: props.autoFocus,
            passwordAsPlaintext: false,
            inputType: this.props.type,
        };
    }

    get _inputProps() {
        return omit(this.props, INPUT_PROP_BLACKLIST);
    }

    get _showPasswordIcon() {
        return this.props.type === 'password';
    }

    get _showClearIcon() {
        return this._usesTextfield
            && this.props.clearable
            && this.state.isFocused
            && !!this.props.value;
    }

    get _usesTextfield() {
        return !NON_TEXT_INPUT_TYPES.includes(this.props.type);
    }

    get _hasFocusedLabel() {
        return this._usesTextfield && (this.state.isFocused || this.props.value);
    }

    get _inputTypeClass() {
        const { inputType } = this.state;

        if (inputType === 'checkbox') {
            return styles.checkbox;
        }

        return styles.textBased;
    }

    _onBlur = (e) => {
        this.setState(() => ({ isFocused: false }));
        this.props.onBlur(e);
    };

    _onFocus = (e) => {
        this.setState(() => ({ isFocused: true }));
        this.props.onFocus(e);
    };

    _onKeyDown = (e) => {
        const {
            key,
            keyCode,
        } = e;

        if (key === 'ArrowUp') {
            this._preventCursorFromJumpingToStartOfInput(e);
        }

        if (this.props.digitsOnly) {
            // backspace, tab, delete, end, home, left, up, right, down, keypad keys
            const specialKeys = [8, 9, 46, 35, 36, 37, 38, 39, 40, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105];
            const keyPressed = String.fromCharCode(keyCode);
            const isNumeric = !isNaN(parseInt(keyPressed, 10));
            const isSpecial = specialKeys.includes(keyCode);

            if (!isNumeric && !isSpecial) {
                return e.preventDefault();
            }
        }

        this.props.onKeyDown(e);
    };

    _preventCursorFromJumpingToStartOfInput(e) {
        return e.preventDefault();
    }

    _onClearClick = (e) => {
        /* the clear-input button is internal to this component, but the result of
         * clicking that button needs to be communicated to consumers of this
         * component. So the button-click event is translated into an input-field-
         * value-has-changed event and passed along via `props.onChange` */

        const fakeEvent = generateFakeOnChangeEvent(e, '');
        this.props.onChange(fakeEvent);
    };

    _onInputCaretClick = (e) => {
        if (this.state.isFocused) {
            return this.input.blur();
        }

        this.input.focus();
    };

    _onTogglePasswordVisibility = () => {
        this.setState((oldState) => ({
            passwordAsPlaintext: !oldState.passwordAsPlaintext,
            inputType: !oldState.passwordAsPlaintext
                ? 'text'
                : 'password',
        }));
    };

    _assignInputRef = (node) => {
        this.input = node;
    };

    render() {
        const {
            className,
            showValidity,
            valid,
            helpMessage,
            placeholder,
            showInputCaret,
            contextHelpClassName,
        } = this.props;

        const {
            isFocused,
            id,
            passwordAsPlaintext,
            inputType,
        } = this.state;

        return (
            <div
                className={classNames(
                    className,
                    styles.container,
                    this._inputTypeClass,
                    {
                        [styles.hasLabel]: !!placeholder,
                        [styles.focusedBorder]: isFocused,
                        [styles.validBorder]: showValidity && valid,
                        [styles.invalidBorder]: showValidity && !valid,
                    },
                )}
            >
                {placeholder && (
                    <label
                        className={classNames(
                            styles.label,
                            { [styles.focusedLabel]: this._hasFocusedLabel },
                        )}
                        htmlFor={id}
                    >
                        {placeholder}
                    </label>
                )}
                <input
                    {...this._inputProps}
                    className={this.props.inputClassName}
                    id={id}
                    onBlur={this._onBlur}
                    onFocus={this._onFocus}
                    onKeyDown={this._onKeyDown}
                    placeholder=""
                    ref={this._assignInputRef}
                    type={inputType}
                />
                <InputIndicators
                    isActive={isFocused}
                    isValid={valid}
                    onClearClick={this._onClearClick}
                    onInputCaretClick={this._onInputCaretClick}
                    onTogglePasswordVisibility={this._onTogglePasswordVisibility}
                    passwordAsPlaintext={passwordAsPlaintext}
                    showClearIcon={this._showClearIcon}
                    showInputCaret={showInputCaret}
                    showPasswordIcon={this._showPasswordIcon}
                    showValidityIcon={showValidity}
                />
                {helpMessage && (
                    <ContextHelp
                        className={classNames(
                            styles.contextHelp,
                            contextHelpClassName,
                        )}
                        message={helpMessage}
                    />
                )}
            </div>
        );
    }
}

export default FormInput;
